import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  gusWeeklyAPI,
  GusWeeklyRecipient,
  GusWeeklyRecipientCreate,
  GusWeeklySuggestion,
} from '../../services/api';
import { errorMessage, isEmail } from './format';
import { focusRing, squareField, squarePaper } from './styles';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: (recipient: GusWeeklyRecipient) => void;
}

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

const AddRecipientDialog: React.FC<Props> = ({ open, onClose, onAdded }) => {
  const [input, setInput] = useState('');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<GusWeeklySuggestion | null>(null);
  const [options, setOptions] = useState<GusWeeklySuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blurred, setBlurred] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    seq.current++;
    if (open) {
      setInput('');
      setName('');
      setPicked(null);
      setOptions([]);
      setError(null);
      setSaving(false);
      setBlurred(false);
    }
  }, [open]);

  // Suggest after two characters, debounced; stale responses are dropped.
  useEffect(() => {
    const q = input.trim();
    if (picked || q.length < MIN_CHARS) {
      seq.current++;
      setOptions([]);
      setSearching(false);
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await gusWeeklyAPI.suggest(q);
        if (mine === seq.current) setOptions(found);
      } catch {
        if (mine === seq.current) setOptions([]);
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      seq.current++;
    };
  }, [input, picked]);

  const showInvalid =
    !picked && input.trim() !== '' && !isEmail(input) && (input.includes('@') || blurred);
  const canSubmit = isEmail(input) && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    const body: GusWeeklyRecipientCreate = { email: input.trim() };
    if (name.trim()) body.name = name.trim();
    if (picked?.admin_user_id) body.admin_user_id = picked.admin_user_id;
    if (picked?.user_id) body.user_id = picked.user_id;
    setSaving(true);
    setError(null);
    try {
      onAdded(await gusWeeklyAPI.add(body));
    } catch (err) {
      setError(errorMessage(err, 'Could not add this recipient'));
      setSaving(false);
    }
  };

  const kindLabel = (s: GusWeeklySuggestion) =>
    s.kind === 'portal_admin' ? 'Portal admin' : `Tenant user${s.project_name ? ` · ${s.project_name}` : ''}`;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={squarePaper}
    >
      <DialogTitle>Add recipient</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search portal admins and tenant users, or type any email address.
        </Typography>
        <Autocomplete
          freeSolo
          componentsProps={{ paper: squarePaper }}
          disabled={saving}
          options={options}
          loading={searching}
          filterOptions={(o) => o}
          inputValue={input}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.email)}
          onInputChange={(_e, value, reason) => {
            if (reason === 'input') {
              setInput(value);
              setBlurred(false);
              if (picked && name === (picked.name ?? '')) setName('');
              setPicked(null);
            } else if (reason === 'clear') {
              setInput('');
              if (picked && name === (picked.name ?? '')) setName('');
              setPicked(null);
            }
          }}
          onChange={(_e, value) => {
            if (value && typeof value !== 'string') {
              setPicked(value);
              setInput(value.email);
              setName(value.name ?? '');
            }
          }}
          renderOption={(props, o) => (
            <li {...props} key={`${o.kind}:${o.email}`}>
              <Box>
                <Typography variant="body2">{o.name || o.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.name ? `${o.email} · ` : ''}
                  {kindLabel(o)}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Email or name"
              error={showInvalid}
              helperText={showInvalid ? 'Enter a valid email address' : undefined}
              onBlur={(e) => {
                setBlurred(true);
                (params.inputProps as any).onBlur?.(e);
              }}
              sx={squareField}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress size={16} aria-label="Searching" /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          label="Name (optional)"
          sx={squareField}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          fullWidth
          margin="normal"
          inputProps={{ maxLength: 200 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1, borderRadius: 0 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ borderRadius: 0, ...focusRing }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          sx={{ borderRadius: 0, ...focusRing }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRecipientDialog;
